'use client';

import { createBrowserClient } from '@supabase/ssr';
import type { SupabaseClient } from '@supabase/supabase-js';

// ─── Startup: Clear Stale / Broken Auth Keys ──────────────────────────────────
// Runs ONCE when this module is first imported (before any client is created).
// Only removes lock keys and PKCE code-verifier leftovers — NOT the live auth token.
if (typeof window !== 'undefined') {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      // Remove lock keys — these cause the AbortError lock contention
      if (key.startsWith('lock:')) {
        keysToRemove.push(key);
        continue;
      }
      // Remove stale PKCE code-verifier keys (OAuth leftovers)
      if (key.startsWith('sb-') && key.endsWith('-code-verifier')) {
        keysToRemove.push(key);
        continue;
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // localStorage unavailable — safe to ignore
  }
}

// ─── Cookie / Storage Helpers ──────────────────────────────────────────────────
const PFX = 'sb_';

const canUseCookies = (() => {
  let cache: boolean | null = null;
  return () => {
    if (typeof document === 'undefined') return false;
    if (cache !== null) return cache;
    const k = '__sb_test__';
    document.cookie = `${k}=1; Path=/; SameSite=None; Secure; Partitioned`;
    cache = document.cookie.includes(k);
    document.cookie = `${k}=; Path=/; Max-Age=0; SameSite=None; Secure`;
    return cache;
  };
})();

const fromCookies = () =>
  typeof document === 'undefined'
    ? []
    : document.cookie
        .split(';')
        .filter(Boolean)
        .map((c) => {
          const [name, ...rest] = c.trim().split('=');
          return { name: name.trim(), value: decodeURIComponent(rest.join('=')) };
        })
        .filter((c) => c.name);

const fromStorage = () => {
  try {
    return Object.keys(localStorage)
      .filter((k) => k.startsWith(PFX))
      .map((k) => ({ name: k.slice(PFX.length), value: localStorage.getItem(k) || '' }));
  } catch {
    return [];
  }
};

const setCookie = (name: string, value: string, options?: any) => {
  let s = `${name}=${encodeURIComponent(value)}; Path=${options?.path || '/'}; SameSite=None; Secure; Partitioned`;
  if (options?.maxAge) s += `; Max-Age=${options.maxAge}`;
  if (options?.domain) s += `; Domain=${options.domain}`;
  if (options?.expires) s += `; Expires=${new Date(options.expires).toUTCString()}`;
  document.cookie = s;
};

// ─── Singleton Client ──────────────────────────────────────────────────────────
// ONE browser client instance for the entire app lifetime.
// Multiple instances competing for the same auth token lock cause:
// "Lock broken by another request with the 'steal' option"
let _clientInstance: SupabaseClient | null = null;

// ─── Global Auth-In-Progress Guard ────────────────────────────────────────────
let _authInProgress = false;

export function isAuthInProgress(): boolean {
  return _authInProgress;
}

export function setAuthInProgress(value: boolean): void {
  _authInProgress = value;
}

// ─── Full Auth Session Reset ───────────────────────────────────────────────────
// Clears ALL Supabase auth-related keys from localStorage and cookies.
// Call this when a broken session is detected (refresh_token_not_found, lock_broken, etc.)
export function clearAllAuthStorage(): void {
  if (typeof window === 'undefined') return;
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (
        key.startsWith('sb-') ||
        key.startsWith('supabase.auth') ||
        key.startsWith('lock:') ||
        key.startsWith(PFX)
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
  // Also clear auth cookies
  try {
    document.cookie.split(';').forEach((c) => {
      const name = c.trim().split('=')[0];
      if (name && (name.startsWith('sb-') || name.startsWith('supabase'))) {
        document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`;
      }
    });
  } catch {
    // ignore
  }
}

export function createClient(): SupabaseClient {
  // In SSR context, always create a fresh instance (no singleton needed server-side)
  if (typeof window === 'undefined') {
    return createBrowserClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
      {
        cookies: {
          getAll: () => [],
          setAll: () => {},
        },
      }
    );
  }

  // On the client, reuse the same instance to avoid lock contention.
  if (!_clientInstance) {
    _clientInstance = createBrowserClient(
      process.env.NEXT_PUBLIC_SUPABASE_URL!,
      process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
      {
        cookies: {
          getAll: () => (canUseCookies() ? fromCookies() : fromStorage()),
          setAll(cookiesToSet) {
            if (typeof document === 'undefined') return;
            if (canUseCookies()) {
              cookiesToSet.forEach(({ name, value, options }) =>
                value
                  ? setCookie(name, value, options)
                  : (document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=None; Secure`)
              );
            } else {
              cookiesToSet.forEach(({ name, value, options }) => {
                try {
                  value
                    ? localStorage.setItem(`${PFX}${name}`, value)
                    : localStorage.removeItem(`${PFX}${name}`);
                } catch {}
                if (value) setCookie(name, value, options);
              });
            }
          },
        },
      }
    );
  }

  return _clientInstance;
}
